// src/helpers/pdfComandas.js
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getComandaById } from "./rutaComandas";

const fmtARS = n => (Number(n || 0)).toLocaleString("es-AR", { style: "currency", currency: "ARS" });
const fmtNum = n =>
  n == null ? "" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = d => {
  const dt = d ? new Date(d) : null;
  return dt ? dt.toLocaleDateString("es-AR") : "";
};

/**
 * genera un PDF de comanda. Acepta el objeto completo o un id (string).
 * Si recibe id, busca la comanda con getComandaById (incluye pagos).
 */
export async function generarPDFComanda(comandaOrId) {
  const resp =
    typeof comandaOrId === "string"
      ? await getComandaById(comandaOrId)
      : { ok: true, comanda: comandaOrId };

  const comanda = resp?.comanda;
  if (!comanda) {
    alert("No se encontró la comanda para generar el PDF.");
    return;
  }

  const pagos = Array.isArray(comanda.pagos) ? comanda.pagos : [];
  const total = Number(comanda.total || 0);
  const totalPagado =
    Number(comanda.totalPagado ?? pagos.reduce((a, p) => a + (Number(p.montoPagado) || 0), 0));
  const saldo = total - totalPagado;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const x = 40;

  // Header
  doc.setFont("helvetica", "bold").setFontSize(16);
  doc.text(`Comanda #${comanda.codcom ?? ""}`, x, 40);
  doc.setFont("helvetica", "normal").setFontSize(10);
  doc.text(`Fecha emisión: ${fmtDate(new Date())}`, 555, 40, { align: "right" });

  // INFO
  const info = [
    ["Empresa", comanda.codemp?.empresa || ""],
    ["Nro viaje", comanda.nroviaje || ""],
    ["Fecha", fmtDate(comanda.fecha)],
    ["Transporte", comanda.codtra?.transporte || ""],
    ["Chofer", comanda.codcho?.nombre || ""],
    ["Cliente Factura", comanda.codclif?.razonsocial || ""],
    ["Cliente Carga", comanda.codclic?.razonsocial || ""],
    ["Cliente Descarga", comanda.codclid?.razonsocial || ""],
    ["Origen", comanda.codori?.destino || ""],
    ["Destino", comanda.coddes?.destino || ""],
    ["Producto", comanda.codpro?.producto || ""],
    ["Camión", comanda.codcam?.camion || ""],
    ["Estado/Situacion", comanda.codest?.estado || ""],
    ["Factura", comanda.factura || ""],
    ["Estado", comanda.estado || ""],
    ["Remitos", comanda.remitos || ""],
    
    ["Kg Sal", fmtNum(comanda.kgsal)],
    ["Kg Lle", fmtNum(comanda.kglle)],
    ["Dif", fmtNum(comanda.dif)],
    ["Km Ori", fmtNum(comanda.kmsal)],
    ["Km Des", fmtNum(comanda.kmdes)],
    ["Observaciones", comanda.observ || ""],
  ];

  doc.autoTable({
    startY: 60,
    head: [["Campo", "Valor"]],
    body: info,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [119, 136, 153] },
  });

  // MONTOS
  let y = doc.lastAutoTable.finalY + 12;
  doc.setFont("helvetica", "bold").text("Montos", x, y);
  doc.autoTable({
    startY: y + 6,
    head: [["Concepto", "Importe"]],
    body: [
      ["Pactado", fmtARS(comanda.pactado)],
      ["SubTotal", fmtARS(comanda.subtotal)],
      ["IVA", fmtARS(comanda.iva)],
      ["Total", fmtARS(total)],
      ["Pactado Tercero", fmtARS(comanda.pactadoter)],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [119, 136, 153] },
    columnStyles: { 1: { halign: "right" } },
  });

  // PAGOS
  y = doc.lastAutoTable.finalY + 12;
  doc.setFont("helvetica", "bold").text("Pagos registrados", x, y);

  const pagosRows = pagos.length
    ? pagos.map((p, i) => [
        i + 1,
        fmtDate(p?.fechaPago),
        p?.metodoPago?.metodo || (typeof p?.metodoPago === "string" ? p.metodoPago : "—"),
        fmtARS(p?.montoPagado),
        p?.orden || "",
        p?.observ || "",
      ])
    : [["-", "-", "-", "-", "Sin pagos"]];

  doc.autoTable({
    startY: y + 6,
    head: [["#", "Fecha", "Método", "Monto", "Orden Pago", "Observaciones"]],
    body: pagosRows,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [119, 136, 153] },
    columnStyles: { 3: { halign: "right" } },
  });

  // RESUMEN
  y = doc.lastAutoTable.finalY + 12;
  doc.setFont("helvetica", "bold").text("Resumen", x, y);
  doc.autoTable({
    startY: y + 6,
    head: [["Total", "Pagado", "Saldo"]],
    body: [[fmtARS(total), fmtARS(totalPagado), fmtARS(saldo)]],
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 6 },
    headStyles: { fillColor: [119, 136, 153] },
    columnStyles: { 0: { halign: "right" }, 1: { halign: "right" }, 2: { halign: "right" } },
  });

  // Footer con páginas
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(`Página ${i} de ${pages}`, 300, 820, { align: "center" });
  }

  doc.save(`comanda_${comanda.codcom || comanda._id}.pdf`);
}
